<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  ref,
  useId,
  type InputHTMLAttributes,
} from 'vue';
import { i18nInjectionKey } from '../internal';
import { FONT_SIZE_LOOKUP, labelInjectionKey, type InputHeight } from './internal';
import { computeFill } from './composables/sliderFill';
import { useInputSaveStatus } from './composables/useInputSaveStatus';
import { debounce } from './composables/debounce';
import NxSaveStateBadge from './NxSaveStateBadge.vue';
import { NxTooltip } from '../Tooltip';
import { ArrowClockwiseIcon, CheckmarkIcon, DismissIcon } from '@blpde/nx-icons';
import {
  nearestSliderMarker,
  orderSliderMarkers,
  sliderMarkerCountText,
  sliderMarkerLane,
  sliderMarkerLaneCount,
  sliderMarkerPosition,
  type NxSliderMarker,
} from './sliderMarkers';

const props = withDefaults(
  defineProps<
    {
      id?: string;
      min?: number;
      max?: number;
      step?: number;
      disabled?: boolean;
      /** Scale of the whole control. Defaults to 's'. */
      height?: InputHeight;
      /** Always-visible value bubble above the thumb. */
      showValue?: boolean;
      /** Start/end labels under the track ends. */
      minMaxLabels?: boolean;
      /** Overrides the start (min) label text. */
      startLabel?: string;
      /** Overrides the end (max) label text. */
      endLabel?: string;
      /** Formats the bubble value and the default min/max labels. */
      format?: (v: number) => string;
      /** Draws tick marks at each step. Intended for coarse steps. */
      ticks?: boolean;
      /** Debounce (ms) before the settled value is emitted via `commit`. 0 = off. */
      commitDebounce?: number;
      /**
       * Clickable marks on a track above the slider (events on a replay
       * timeline, chapters on a scrubber), placed on the slider's own scale so
       * a mark at `position` sits exactly where the thumb would. Given — even
       * empty — the track shows, with a line at the current value. A click,
       * Enter or Space emits `marker-select`; the value does not move by
       * itself, the consumer seeks. Keyboard: the marks are one tab stop,
       * arrows / Home / End walk them in track order.
       */
      markers?: NxSliderMarker[];
      /** Accessible name of the marker list. Default: kit text „Markierungen“. */
      markersLabel?: string;
    } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value' | 'min' | 'max' | 'step'>
  >(),
  {
    id: undefined,
    min: 0,
    max: 100,
    step: 1,
    disabled: false,
    height: 's',
    showValue: false,
    minMaxLabels: false,
    startLabel: undefined,
    endLabel: undefined,
    format: undefined,
    ticks: false,
    commitDebounce: 400,
    markers: undefined,
    markersLabel: undefined,
  }
);

const model = defineModel<number>({ required: true });

/** Settled value, debounced. Consumers hang their save here (not on v-model),
 * so keyboard stepping / dragging collapses into one commit and the save status
 * does not flicker. */
const emit = defineEmits<{ commit: [value: number]; 'marker-select': [marker: NxSliderMarker] }>();

// Label association — mirror NxSwitch / NxNativeSelect.
let elementId = props.id;
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) {
  elementId ??= useId();
  labelInjection.setFor(elementId);
}

defineOptions({ inheritAttrs: false });

const fill = computed(() => computeFill(model.value, props.min, props.max));

const fmt = (v: number) => (props.format ? props.format(v) : String(v));
const valueText = computed(() => fmt(model.value));
const startText = computed(() => props.startLabel ?? fmt(props.min));
const endText = computed(() => props.endLabel ?? fmt(props.max));

// Tick positions as plain percentages along the range; corrected for thumb
// inset in CSS via the shared position formula.
const tickMarks = computed<number[]>(() => {
  if (!props.ticks) return [];
  const span = props.max - props.min;
  if (span <= 0 || props.step <= 0) return [];
  const count = Math.floor(span / props.step + 1e-9);
  const marks: number[] = [];
  for (let i = 0; i <= count; i++) marks.push((i * props.step * 100) / span);
  return marks;
});

// Save status (loading/success/error) from an ancestor NxLoadingWrapper.
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: statusDisabled,
} = useInputSaveStatus();
// Lock interaction while loading (like the text inputs), but keep the loading
// shimmer instead of the grey disabled look — is-disabled tracks props.disabled only.
const interactionDisabled = computed(() => props.disabled || statusDisabled.value);

const commitDebounced = debounce((v: number) => emit('commit', v), props.commitDebounce);
function onCommit() {
  commitDebounced.call(model.value);
}
function onBlur() {
  commitDebounced.flush();
}
onBeforeUnmount(() => commitDebounced.cancel());

// --- Markers -----------------------------------------------------------------
const i18n = inject(i18nInjectionKey, null);
const markerListLabel = computed(() => {
  if (props.markersLabel) return props.markersLabel;
  const key = 'common.slider.markers';
  const text = i18n?.t(key);
  return text && text !== key ? text : 'Markierungen';
});
const orderedMarkers = computed(() => orderSliderMarkers(props.markers ?? []));
const markerLanes = computed(() => sliderMarkerLaneCount(orderedMarkers.value));
const markerList = ref<HTMLOListElement | null>(null);
/** Marker holding the tab stop once the user walked the list; else nearest to the value. */
const focusedMarkerId = ref<string | null>(null);
const tabStopIndex = computed(() => {
  const ordered = orderedMarkers.value;
  const kept = ordered.findIndex((marker) => marker.id === focusedMarkerId.value);
  if (kept >= 0) return kept;
  return nearestSliderMarker(ordered, fill.value / 100);
});

function markerStyle(marker: NxSliderMarker): Record<string, string | number> {
  return {
    '--marker-pos': sliderMarkerPosition(marker.position),
    '--marker-lane': sliderMarkerLane(marker.lane),
  };
}

function selectMarker(marker: NxSliderMarker): void {
  if (interactionDisabled.value) return;
  focusedMarkerId.value = marker.id;
  emit('marker-select', marker);
}

function onMarkerKeydown(event: KeyboardEvent): void {
  const ordered = orderedMarkers.value;
  if (interactionDisabled.value || ordered.length === 0) return;
  const last = ordered.length - 1;
  const current = Math.max(0, tabStopIndex.value);
  let next: number;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = Math.min(last, current + 1);
  else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = Math.max(0, current - 1);
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = last;
  else return;
  event.preventDefault();
  focusedMarkerId.value = ordered[next]!.id;
  void nextTick(() =>
    markerList.value?.querySelectorAll<HTMLButtonElement>('.marker')[next]?.focus()
  );
}
</script>

<template>
  <div
    class="nx-slider"
    :class="{ 'is-disabled': disabled }"
    :data-save-status="visualState"
    :style="{ fontSize: FONT_SIZE_LOOKUP[height], '--slider-fill': fill }"
  >
    <div
      v-if="markers"
      class="marker-track"
      :style="{ '--slider-marker-lanes': markerLanes }"
      data-testid="nx-slider-markers"
    >
      <ol
        ref="markerList"
        class="marker-list"
        :aria-label="markerListLabel"
        @keydown="onMarkerKeydown"
      >
        <li
          v-for="(marker, index) in orderedMarkers"
          :key="marker.id"
          class="marker-slot"
          :style="markerStyle(marker)"
        >
          <button
            type="button"
            class="marker"
            :class="[`shape-${marker.shape ?? 'dot'}`, `tone-${marker.tone ?? 'default'}`]"
            :data-marker-id="marker.id"
            :aria-label="marker.label"
            :title="marker.label"
            :disabled="interactionDisabled"
            :tabindex="index === tabStopIndex ? 0 : -1"
            @click="selectMarker(marker)"
          >
            <span class="marker-glyph" aria-hidden="true" />
            <span
              v-if="sliderMarkerCountText(marker.count)"
              class="marker-count"
              aria-hidden="true"
            >
              {{ sliderMarkerCountText(marker.count) }}
            </span>
          </button>
        </li>
      </ol>
      <span class="marker-cursor" aria-hidden="true" />
    </div>
    <div class="track-row">
      <input
        :id="elementId"
        v-bind="{ ...$attrs, ...ariaBindings }"
        v-model.number="model"
        type="range"
        class="range"
        :min="min"
        :max="max"
        :step="step"
        :disabled="interactionDisabled"
        :aria-valuetext="format ? valueText : undefined"
        @change="onCommit"
        @blur="onBlur"
      />
      <span
        v-for="(pos, i) in tickMarks"
        :key="`tick-${i}`"
        class="tick"
        aria-hidden="true"
        :style="{ '--tick': pos }"
      />
      <!-- With showValue the bubble owns the badge slot, so it absorbs the save
           status itself: it recolors and gains an inline status glyph before the
           value. Track and thumb always keep the brand color. -->
      <output v-if="showValue" class="bubble" :data-save-status="visualState" aria-hidden="true">
        <NxTooltip
          v-if="visualState === 'error' && errorMessage"
          :text="errorMessage"
          side="top"
          class="bubble-tip"
        >
          <span class="bubble-content">
            <DismissIcon filled class="nxt-icon-xs" />
            {{ valueText }}
          </span>
        </NxTooltip>
        <span v-else class="bubble-content">
          <ArrowClockwiseIcon v-if="visualState === 'loading'" filled class="nxt-icon-sm" />
          <CheckmarkIcon v-else-if="visualState === 'success'" filled class="nxt-icon-xs" />
          {{ valueText }}
        </span>
      </output>
      <!-- Without the bubble, the badge floats in the bubble's slot and follows
           the thumb — inside the track bounds even at 0% / 100%. -->
      <NxSaveStateBadge
        v-else
        class="save-badge"
        :state="visualState"
        :error-message="errorMessage"
        :fade-out="fadeOut"
      />
    </div>
    <div v-if="minMaxLabels" class="minmax" aria-hidden="true">
      <span>{{ startText }}</span>
      <span>{{ endText }}</span>
    </div>
    <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
  </div>
</template>

<style scoped>
.nx-slider {
  /* Geometry in em so the whole control scales off the inherited font-size.
     These are local layout state, not design tokens (no --nxt- prefix). */
  --slider-thumb: 1em;
  --slider-track-h: 0.375em;
  /* Thumb-inset-corrected position of the current value along the track. */
  --slider-pos: calc(
    var(--slider-thumb) / 2 + (100% - var(--slider-thumb)) * var(--slider-fill) / 100
  );
  display: inline-flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: 100%;
}

.track-row {
  position: relative;
  display: flex;
  align-items: center;
  height: var(--slider-thumb);
}

.range {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: var(--slider-thumb);
  margin: 0;
  background: transparent;
  cursor: pointer;
}

/* WebKit/Blink: no filled-track pseudo-element → paint the fill as a gradient. */
.range::-webkit-slider-runnable-track {
  height: var(--slider-track-h);
  border-radius: var(--nxt-radius-full);
  background: linear-gradient(
    to right,
    var(--nxt-slider-track-fill) 0,
    var(--nxt-slider-track-fill) var(--slider-pos),
    var(--nxt-slider-track-bg) var(--slider-pos),
    var(--nxt-slider-track-bg) 100%
  );
}
.range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: var(--slider-thumb);
  height: var(--slider-thumb);
  margin-top: calc((var(--slider-track-h) - var(--slider-thumb)) / 2);
  border-radius: 50%;
  background: var(--nxt-slider-thumb-bg);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-slider-thumb-border);
}

/* Firefox: native progress pseudo-element colors the filled part accurately. */
.range::-moz-range-track {
  height: var(--slider-track-h);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-slider-track-bg);
}
.range::-moz-range-progress {
  height: var(--slider-track-h);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-slider-track-fill);
}
.range::-moz-range-thumb {
  width: var(--slider-thumb);
  height: var(--slider-thumb);
  border-radius: 50%;
  background: var(--nxt-slider-thumb-bg);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-slider-thumb-border);
}

/* Focus ring on the thumb — the always-on --nxt-slider-thumb-border already
   gives the thumb a crisp brand-colored edge, so focus only needs to add the
   soft halo (--nxt-slider-ring, a soft/translucent ring like every other
   input's --nxt-*-ring) instead of the old two-layer solid+subtle box-shadow. */
.range:focus-visible {
  outline: none;
}
.range:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 0.1875em var(--nxt-slider-ring);
}
.range:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 0.1875em var(--nxt-slider-ring);
}

/* Disabled: mute brand → disabled tokens, block interaction, show the blocked
   cursor. The range has pointer-events: none, so the not-allowed cursor lives
   on the wrapper (which still receives hover). */
.nx-slider.is-disabled {
  cursor: not-allowed;
}
.nx-slider.is-disabled .range {
  cursor: not-allowed;
  pointer-events: none;
}
.nx-slider.is-disabled .range::-webkit-slider-runnable-track {
  background: linear-gradient(
    to right,
    var(--nxt-disabled-fg) 0,
    var(--nxt-disabled-fg) var(--slider-pos),
    var(--nxt-disabled-bg) var(--slider-pos),
    var(--nxt-disabled-bg) 100%
  );
}
.nx-slider.is-disabled .range::-webkit-slider-thumb {
  background: var(--nxt-disabled-fg);
  border-color: var(--nxt-disabled-fg);
}
.nx-slider.is-disabled .range::-moz-range-track {
  background: var(--nxt-disabled-bg);
}
.nx-slider.is-disabled .range::-moz-range-progress {
  background: var(--nxt-disabled-fg);
}
.nx-slider.is-disabled .range::-moz-range-thumb {
  background: var(--nxt-disabled-fg);
  border-color: var(--nxt-disabled-fg);
}
/* Value bubble: mute to the disabled greyscale too. */
.nx-slider.is-disabled .bubble {
  background: var(--nxt-disabled-fg);
  color: var(--nxt-fg-on-accent);
}

/* Loading locks interaction (the range is natively disabled) — show the blocked
   cursor and fade the track/thumb like the other inputs (opacity on the range
   only, so the badge/bubble siblings stay fully visible). Colors stay brand,
   not the disabled grey. */
.nx-slider[data-save-status='loading'],
.nx-slider[data-save-status='loading'] .range {
  cursor: not-allowed;
}
.nx-slider[data-save-status='loading'] .range {
  opacity: 0.5;
}

.tick {
  position: absolute;
  top: 50%;
  left: calc(var(--slider-thumb) / 2 + (100% - var(--slider-thumb)) * var(--tick) / 100);
  width: 0.0625em;
  height: 0.375em;
  transform: translate(-50%, -50%);
  background: var(--nxt-border-strong);
  pointer-events: none;
}

.bubble {
  position: absolute;
  bottom: calc(100% + 0.25em);
  left: var(--slider-pos);
  transform: translateX(-50%);
  padding: 0.125em var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  font-size: 0.875em;
  line-height: var(--nxt-line-height-tight);
  white-space: nowrap;
  pointer-events: none;
  transition: background-color var(--nxt-motion-fast);
}

.minmax {
  display: flex;
  justify-content: space-between;
  color: var(--nxt-fg-muted);
  font-size: 0.875em;
}

/* --- Markers ---
   A track above the range, on the same thumb-inset scale as the value, so a
   mark at position p sits where the thumb stands at p. Lanes stack the marks
   in rows; the cursor line follows the value down to the range. */
.marker-track {
  --slider-marker-size: 0.75em;
  --slider-marker-pitch: 0.875em;
  position: relative;
  height: calc(0.5em + var(--slider-marker-lanes) * var(--slider-marker-pitch));
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}
.marker-list {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}
.marker-slot {
  position: absolute;
  z-index: 1;
  top: calc(0.5em + var(--marker-lane) * var(--slider-marker-pitch));
  left: calc(var(--slider-thumb) / 2 + (100% - var(--slider-thumb)) * var(--marker-pos));
  display: flex;
  transform: translateX(-50%);
}
.marker {
  /* The glyph's colours; shapes and tones only swap these. */
  --marker-fill: var(--nxt-info-bg);
  --marker-ring: color-mix(in srgb, var(--marker-fill) 52%, transparent);
  --marker-halo: transparent;
  position: relative;
  display: grid;
  width: var(--slider-marker-size);
  height: var(--slider-marker-size);
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: transparent;
  cursor: pointer;
}
.marker:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.marker-glyph {
  display: block;
  width: 100%;
  height: 100%;
  border: var(--nxt-hairline) solid var(--nxt-surface-bg);
  border-radius: inherit;
  background: var(--marker-fill);
  box-shadow:
    0 0 0 var(--nxt-hairline) var(--marker-ring),
    0 0 0 calc(var(--nxt-hairline) * 3) var(--marker-halo);
}
.marker.shape-diamond {
  --marker-fill: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-sm);
}
.marker.shape-diamond .marker-glyph {
  transform: rotate(45deg) scale(0.8);
}
.marker.shape-split .marker-glyph {
  background: linear-gradient(135deg, var(--nxt-info-bg) 0 48%, var(--nxt-brand-bg) 52% 100%);
}
.marker.tone-muted {
  --marker-fill: var(--nxt-fg-muted);
}
.marker.tone-warning {
  --marker-ring: var(--nxt-warning-bg);
  --marker-halo: var(--nxt-warning-subtle-bg);
}
.marker.tone-danger {
  --marker-fill: var(--nxt-danger-bg);
  --marker-ring: var(--nxt-danger-bg);
  --marker-halo: var(--nxt-danger-subtle-bg);
}
.marker.tone-muted .marker-glyph,
.marker.tone-danger .marker-glyph {
  background: var(--marker-fill);
}
.marker-count {
  position: absolute;
  z-index: 2;
  bottom: 55%;
  left: 55%;
  min-width: 1.25em;
  padding: 0 0.25em;
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  font-size: 0.625em;
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.6;
  text-align: center;
  pointer-events: none;
}
.marker-cursor {
  position: absolute;
  top: 0;
  bottom: calc(var(--nxt-space-xs) * -1);
  left: var(--slider-pos);
  width: var(--nxt-hairline);
  background: var(--nxt-slider-track-fill);
  pointer-events: none;
}
.nx-slider.is-disabled .marker,
.nx-slider[data-save-status='loading'] .marker {
  cursor: not-allowed;
}
.nx-slider.is-disabled .marker {
  --marker-fill: var(--nxt-disabled-fg);
  --marker-ring: var(--nxt-disabled-bg);
  --marker-halo: transparent;
}
.nx-slider.is-disabled .marker-glyph {
  background: var(--marker-fill);
}
.nx-slider.is-disabled .marker-count {
  background: var(--nxt-disabled-fg);
  color: var(--nxt-fg-on-accent);
}
.nx-slider.is-disabled .marker-cursor {
  background: var(--nxt-disabled-fg);
}

/* --- I-422 save status: corner badge / status bubble ---
   Track and thumb NEVER recolor — the status lives in the bubble slot above the
   thumb. Without showValue an NxSaveStateBadge floats there; with showValue the
   bubble itself recolors and carries the glyph (loading keeps the brand bg). */
.save-badge {
  position: absolute;
  bottom: calc(100% + 0.25em);
  left: var(--slider-pos);
  transform: translateX(-50%);
}
.bubble[data-save-status='success'] {
  background: var(--nxt-success-bg);
  color: var(--nxt-success-fg);
}
.bubble[data-save-status='error'] {
  background: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
  /* The bubble is the tooltip hover target while it shows the error. */
  pointer-events: auto;
}
.bubble-content {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
/* NxTooltip's wrapper div defaults to inline-block; keep the bubble's inline flow. */
.bubble-tip {
  display: inline-flex;
}
.nx-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
